/* eslint-disable */
/* prettier-ignore */
// Generated by elegant-router
// Read more: https://github.com/soybeanjs/elegant-router

import type { RouteRecordRaw, RouteComponent } from 'vue-router';
import type { ElegantConstRoute } from '@elegant-router/vue';
import type { RouteMap, RouteKey, RoutePath } from '@elegant-router/types';

/**
 * transform elegant const routes to vue routes
 * @param routes elegant const routes
 * @param layouts layout components
 * @param views view components
 */
export function transformElegantRoutesToVueRoutes(
  routes: ElegantConstRoute[],
  layouts: Record<string, RouteComponent | (() => Promise<RouteComponent>)>,
  views: Record<string, RouteComponent | (() => Promise<RouteComponent>)>
) {
  return routes.flatMap(route => transformElegantRouteToVueRoute(route, layouts, views));
}

/**
 * transform elegant route to vue route
 * @param route elegant const route
 * @param layouts layout components
 * @param views view components
 */
function transformElegantRouteToVueRoute(
  route: ElegantConstRoute,
  layouts: Record<string, RouteComponent | (() => Promise<RouteComponent>)>,
  views: Record<string, RouteComponent | (() => Promise<RouteComponent>)>
) {
  const LAYOUT_PREFIX = 'layout.';
  const VIEW_PREFIX = 'view.';
  const ROUTE_DEGREE_SPLITTER = '_';
  const FIRST_LEVEL_ROUTE_COMPONENT_SPLIT = '$';

  function isLayout(component: string) {
    return component.startsWith(LAYOUT_PREFIX);
  }

  function getLayoutName(component: string) {
    return component.replace(LAYOUT_PREFIX, '');
  }

  function isView(component: string) {
    return component.startsWith(VIEW_PREFIX);
  }

  function getViewName(component: string) {
    return component.replace(VIEW_PREFIX, '');
  }

  function isFirstLevelRoute(item: ElegantConstRoute) {
    return !item.name.includes(ROUTE_DEGREE_SPLITTER);
  }

  function isSingleLevelRoute(item: ElegantConstRoute) {
    return isFirstLevelRoute(item) && !item.children?.length;
  }

  function getSingleLevelRouteComponent(component: string) {
    const [layout, view] = component.split(FIRST_LEVEL_ROUTE_COMPONENT_SPLIT);

    return {
      layout: getLayoutName(layout),
      view: getViewName(view)
    };
  }

  const vueRoutes: RouteRecordRaw[] = [];

  // add props: true to route
  if (route.path.includes(':') && !route.props) {
    route.props = true;
  }

  const { name, path, component, children, ...rest } = route;

  const vueRoute = { name, path, ...rest } as RouteRecordRaw;

  if (component) {
    if (isSingleLevelRoute(route)) {
      const { layout, view } = getSingleLevelRouteComponent(component);

      const singleLevelRoute: RouteRecordRaw = {
        path,
        component: layouts[layout],
        children: [
          {
            name,
            path: '',
            component: views[view],
            ...rest
          } as RouteRecordRaw
        ]
      };

      return [singleLevelRoute];
    }

    if (isLayout(component)) {
      const layoutName = getLayoutName(component);

      vueRoute.component = layouts[layoutName];
    }

    if (isView(component)) {
      const viewName = getViewName(component);

      vueRoute.component = views[viewName];
    }

  }
  
  // add redirect to child
  if (children?.length && !vueRoute.redirect) {
    vueRoute.redirect = {
      name: children[0].name
    };
  }
  
  if (children?.length) {
    const childRoutes = children.flatMap(child => transformElegantRouteToVueRoute(child, layouts, views));

    if(isFirstLevelRoute(route)) {
      vueRoute.children = childRoutes;
    } else {
      vueRoutes.push(...childRoutes);
    }
  }

  vueRoutes.unshift(vueRoute);

  return vueRoutes;
}

/**
 * map of route name and route path
 */
const routeMap: RouteMap = {
  "root": "/",
  "not-found": "/:pathMatch(.*)*",
  "403": "/403",
  "404": "/404",
  "500": "/500",
  "application-activity-manage": "/application-activity-manage",
  "home": "/home",
  "iframe-page": "/iframe-page/:url",
  "login": "/login/:id?/:module(pwd-login|code-login|tenant-login)?",
  "manage-commerce": "/manage-commerce",
  "manage-commerce_association-information": "/manage-commerce/association-information",
  "manage-commerce_association-journal": "/manage-commerce/association-journal",
  "manage-commerce_association-style": "/manage-commerce/association-style",
  "manage-commerce_contact-information": "/manage-commerce/contact-information",
  "manage-commerce_expert-database": "/manage-commerce/expert-database",
  "manage-commerce_job-application": "/manage-commerce/job-application",
  "manage-commerce_manage-enterprise": "/manage-commerce/manage-enterprise",
  "manage-commerce_manage-member": "/manage-commerce/manage-member",
  "manage-commerce_management-meetings": "/manage-commerce/management-meetings",
  "manage-commerce_membership-application": "/manage-commerce/membership-application",
  "manage-commerce_visit-clock": "/manage-commerce/visit-clock",
  "manage-financial": "/manage-financial",
  "manage-financial_incomexpen-details": "/manage-financial/incomexpen-details",
  "manage-financial_invoice-application": "/manage-financial/invoice-application",
  "manage-financial_monthly-ledger": "/manage-financial/monthly-ledger",
  "manage-financial_overview": "/manage-financial/overview",
  "manage-financial_payment-approval": "/manage-financial/payment-approval",
  "manage-financial_payment-list": "/manage-financial/payment-list",
  "manage-financial_points-details": "/manage-financial/points-details",
  "manage-system": "/manage-system",
  "manage-system_data-option": "/manage-system/data-option",
  "manage-system_manage-dict": "/manage-system/manage-dict",
  "manage-system_manage-flow": "/manage-system/manage-flow",
  "manage-system_manage-flow_flow-category": "/manage-system/manage-flow/flow-category",
  "manage-system_manage-flow_flow-deploy": "/manage-system/manage-flow/flow-deploy",
  "manage-system_manage-flow_flow-model": "/manage-system/manage-flow/flow-model",
  "manage-system_manage-role": "/manage-system/manage-role",
  "manage-system_manage-user": "/manage-system/manage-user",
  "manage-system_menu": "/manage-system/menu",
  "manage-system_message-setting": "/manage-system/message-setting",
  "manage-system_param-setting": "/manage-system/param-setting",
  "manage-system_payment-setting": "/manage-system/payment-setting",
  "manage-system_system-log": "/manage-system/system-log",
  "manage-system_system-setting": "/manage-system/system-setting",
  "manage-system_timed-task": "/manage-system/timed-task",
  "operate-manage": "/operate-manage",
  "operate-manage_activity-manage": "/operate-manage/activity-manage",
  "operate-manage_advertisement-manage": "/operate-manage/advertisement-manage",
  "operate-manage_album-manage": "/operate-manage/album-manage",
  "operate-manage_application-tools": "/operate-manage/application-tools",
  "operate-manage_info-manage": "/operate-manage/info-manage",
  "operate-manage_party-group-manage": "/operate-manage/party-group-manage",
  "operate-manage_supply-hall": "/operate-manage/supply-hall",
  "operate-manage_video-manage": "/operate-manage/video-manage",
  "personal": "/personal",
  "statistical-analysis": "/statistical-analysis",
  "statistical-analysis_commerce-statisticcal": "/statistical-analysis/commerce-statisticcal",
  "statistical-analysis_finance-statistical": "/statistical-analysis/finance-statistical",
  "statistical-analysis_member-statistical": "/statistical-analysis/member-statistical",
  "statistical-analysis_operations-statistical": "/statistical-analysis/operations-statistical",
  "statistical-analysis_points-statistics": "/statistical-analysis/points-statistics",
  "tenant-manage": "/tenant-manage",
  "tenant-manage_tenant-list": "/tenant-manage/tenant-list"
};

/**
 * get route path by route name
 * @param name route name
 */
export function getRoutePath<T extends RouteKey>(name: T) {
  return routeMap[name];
}

/**
 * get route name by route path
 * @param path route path
 */
export function getRouteName(path: RoutePath) {
  const routeEntries = Object.entries(routeMap) as [RouteKey, RoutePath][];

  const routeName: RouteKey | null = routeEntries.find(([, routePath]) => routePath === path)?.[0] || null;

  return routeName;
}
